import { formatText, type Label, type Locale } from "../texts";

// Reglas de Error Hub (R7) sin React: títulos, módulos, estados, duraciones y la dirección compartida por las cinco
// secciones. Se prueban en hub.test.tsx.

export type HubIssue = {
  id: string;
  title: string | null;
  errorKey: string | null;
  module: string | null;
  surface: string | null;
  category: string | null;
  status: string | null;
  severity: string | null;
  firstSeen: string | null;
  lastSeen: string | null;
  occurrences: number;
  users: number;
  organizations: number;
  release: string | null;
};

export type HubOccurrence = {
  id: string;
  issueId?: string;
  reference: string | null;
  occurredAt: string | null;
  errorKey: string | null;
  module: string | null;
  userName: string | null;
  userEmail: string | null;
  organizationName: string | null;
  traceId: string | null;
  release: string | null;
};

export type HubOperation = {
  operation: string | null;
  module: string | null;
  release: string | null;
  count: number;
  errors: number;
  p95: number | null;
  variation: number | null;
};

export type HubRelease = {
  release: string | null;
  count: number;
  errors: number;
  errorRate: number | null;
  p95: number | null;
  newIssues: number;
  regressions: number;
  occurrences: number;
};

/**
 * Filtros que viajan entre las cinco secciones (período, entorno, módulo y versión); la búsqueda y la página son de cada
 * una. El entorno (`env`, R8) es el nombre que guarda el Error Hub.
 */
export const sharedKeys = ["period", "env", "module", "release"] as const;

/** Los filtros compartidos de la dirección actual, para los enlaces entre secciones (E84). */
export function sharedQuery(params: URLSearchParams, extra: Record<string, string | null | undefined> = {}): string {
  const next = new URLSearchParams();
  for (const key of sharedKeys) {
    const value = params.get(key);
    if (value) next.set(key, value);
  }
  for (const [key, value] of Object.entries(extra)) {
    if (value) next.set(key, value);
    else next.delete(key);
  }
  const text = next.toString();
  return text ? `?${text}` : "";
}

/**
 * Nombre de un entorno en palabras (R8): LOCAL, DEV o Development, PRE-PRO o Staging, PRO o Production; otro nombre se
 * muestra tal como lo guardó el Error Hub (es el nombre que le puso quien lo instaló).
 */
export function environmentName(L: Label, value: string | null | undefined): string {
  if (!value) return "—";
  const code = value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_");
  const key =
    code === "LOCAL"
      ? "ADM_HUB_ENV_LOCAL"
      : code === "DEV" || code === "DEVELOPMENT"
        ? "ADM_HUB_ENV_DEV"
        : code === "PRE_PRO" || code === "PREPRO" || code === "STAGING"
          ? "ADM_HUB_ENV_PREPRO"
          : code === "PRO" || code === "PROD" || code === "PRODUCTION"
            ? "ADM_HUB_ENV_PRO"
            : null;
  if (!key) return value;
  const text = L(key);
  return text.startsWith("⟦") ? value : text;
}

/** Módulos del Error Hub, en el orden del filtro. */
export const hubModules = ["MSG", "AI", "ID", "PLT", "CRM", "ERP", "AGT", "EMP", "KNW", "CAT"] as const;

/** Nombre del módulo («Mensajería»); uno que no conoce la pantalla dice «Bheli». */
export function moduleName(L: Label, code: string | null | undefined): string {
  const text = code ? L(`ADM_HUB_MODULE_${code}`) : "";
  return text && !text.startsWith("⟦") ? text : L("ADM_HUB_MODULE_OTHER");
}

/** Nombre de la superficie (API, Portal, App…) o de la categoría (Integración, Datos…), con el código si no hay texto. */
function known(L: Label, prefix: string, code: string | null | undefined): string | null {
  if (!code) return null;
  const text = L(`${prefix}${code}`);
  return text.startsWith("⟦") ? null : text;
}

/**
 * Título del problema en palabras: el título que guardó el Error Hub si ya es una frase; si es la clave técnica (lo
 * habitual), la explicación por tipo como hoy («Error inesperado en Mensajería», «Página no encontrada»…). D7: la clave
 * queda en el contexto técnico.
 */
export function issueTitle(
  L: Label,
  item: { title?: string | null; errorKey?: string | null; module?: string | null },
): string {
  const key = item.errorKey ?? "";
  const title = item.title?.trim() ?? "";
  if (title && title !== key && !/^[A-Z0-9_]+$/.test(title)) return title;
  if (key === "BROWSER_UNHANDLED" || key === "BROWSER_RENDER_FAILED") return L("ADM_HUB_TITLE_BROWSER");
  if (key === "HTTP_404") return L("ADM_HUB_TITLE_NOT_FOUND");
  if (key === "HTTP_401") return L("ADM_HUB_TITLE_UNAUTHORIZED");
  return formatText(L("ADM_HUB_TITLE_UNEXPECTED"), moduleName(L, item.module));
}

/** Segunda línea del problema: «Mensajería · API · Integración». */
export function issueLine(
  L: Label,
  item: { module?: string | null; surface?: string | null; category?: string | null },
) {
  return [
    moduleName(L, item.module),
    known(L, "ADM_HUB_SURFACE_", item.surface),
    known(L, "ADM_HUB_CATEGORY_", item.category),
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Estado del problema en palabras con su tono: regresión ocre, nuevo de atención, resuelto e ignorado neutros. */
export function statusView(
  L: Label,
  status: string | null | undefined,
): { text: string; tone: "ochre" | "accent" | "plain" } {
  const text = L(`ADM_HUB_STATE_${status ?? ""}`);
  const label = text.startsWith("⟦") ? L("USAGE_UNKNOWN") : text;
  if (status === "REGRESSION") return { text: label, tone: "ochre" };
  if (status === "NEW") return { text: label, tone: "accent" };
  return { text: label, tone: "plain" };
}

export function severityText(L: Label, severity: string | null | undefined): string {
  const text = L(`ADM_HUB_SEVERITY_${severity ?? ""}`);
  return text.startsWith("⟦") ? "—" : text;
}

/** Estados que se eligen en «Gestionar…» y en el filtro (los del Error Hub). */
export const issueStates = ["NEW", "INVESTIGATING", "REGRESSION", "RESOLVED", "IGNORED"] as const;
export const issueSeverities = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"] as const;

/**
 * La acción rápida del panel según el estado, como hoy: nuevo o resuelto → «Iniciar investigación» / «Reabrir»; en
 * investigación → «Marcar como resuelto»; regresión o ignorado → la misma «Iniciar investigación». Null: ninguna.
 */
export function quickAction(status: string | null | undefined): { next: string; key: string; toast: string } | null {
  switch (status) {
    case "NEW":
    case "REGRESSION":
    case "IGNORED":
      return { next: "INVESTIGATING", key: "ADM_HUB_START_INVESTIGATION", toast: "ADM_HUB_INVESTIGATING_TOAST" };
    case "INVESTIGATING":
      return { next: "RESOLVED", key: "ADM_HUB_RESOLVE", toast: "ADM_HUB_RESOLVED_TOAST" };
    case "RESOLVED":
      return { next: "INVESTIGATING", key: "ADM_HUB_REOPEN", toast: "ADM_HUB_REOPENED_TOAST" };
    default:
      return null;
  }
}

/** Una duración en ms: «310 ms» o «1.9 s» con el formateador del idioma (E87); sin dato, «—». */
export function durationText(locale: Locale, ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms).toLocaleString(locale)} ms`;
  return `${(ms / 1000).toLocaleString(locale, { maximumFractionDigits: 1 })} s`;
}

/** Un porcentaje con un decimal («0.9 %»); sin dato, «—». */
export function percentText(locale: Locale, value: number | null | undefined, digits = 1): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits })} %`;
}

/** Variación con signo («+18 %», «−6 %», «0 %»); sin período anterior, «—». */
export function variationText(locale: Locale, value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  const rounded = Math.round(value);
  const sign = rounded > 0 ? "+" : rounded < 0 ? "−" : "";
  return `${sign}${Math.abs(rounded).toLocaleString(locale)} %`;
}

/** Estabilidad del período: 100 − tasa de errores (sin operaciones medidas no hay estabilidad: null). */
export const stability = (errorRate: number | null | undefined) => (errorRate == null ? null : 100 - errorRate);

/**
 * RC1 · K3 (C32 1.14.9, E54): una comparación con el período anterior. `none`: no hay período anterior (se muestra el
 * contexto, nunca «0 %» ni «0 puntos»); `same`: la diferencia no se ve en los números mostrados («Sin cambios»);
 * `change`: la diferencia con su signo, tal como se muestra.
 */
export type Change = { kind: "none" } | { kind: "same" } | { kind: "change"; text: string };

const sign = (value: number) => (value > 0 ? "+" : value < 0 ? "−" : "");

/** Un porcentaje con signo que nunca dice «0 %»: entero desde 1 %, con un decimal debajo; null si redondea a 0. */
function signedPercent(locale: Locale, value: number): string | null {
  const digits = Math.abs(value) >= 1 ? 0 : 1;
  const shown = Number(Math.abs(value).toFixed(digits));
  if (shown === 0) return null;
  return `${sign(value)}${shown.toLocaleString(locale, { maximumFractionDigits: digits })} %`;
}

/** Estabilidad en puntos: la diferencia entre los dos valores como se muestran (un decimal). */
export function pointsChange(locale: Locale, now: number | null, before: number | null, hasPrevious: boolean): Change {
  if (!hasPrevious || now == null || before == null) return { kind: "none" };
  const diff = Math.round(now * 10) / 10 - Math.round(before * 10) / 10;
  const shown = Math.round(diff * 10) / 10;
  if (shown === 0) return { kind: "same" };
  return {
    kind: "change",
    text: `${sign(shown)}${Math.abs(shown).toLocaleString(locale, { maximumFractionDigits: 1 })}`,
  };
}

/** P95 en %: «Sin cambios» si los dos tiempos se muestran iguales («5 s» y «5 s»). */
export function durationChange(
  locale: Locale,
  now: number | null,
  before: number | null,
  hasPrevious: boolean,
): Change {
  if (!hasPrevious || now == null || before == null || before <= 0) return { kind: "none" };
  if (durationText(locale, now) === durationText(locale, before)) return { kind: "same" };
  const text = signedPercent(locale, ((now - before) / before) * 100);
  return text ? { kind: "change", text } : { kind: "same" };
}

/** Variación de una operación en Rendimiento: «—» sin período anterior, «Sin cambios» si redondea a 0 (nunca «0 %»). */
export function variationView(locale: Locale, value: number | null | undefined): Change {
  if (value == null || Number.isNaN(value)) return { kind: "none" };
  const rounded = Math.round(value);
  if (rounded === 0) return { kind: "same" };
  return { kind: "change", text: variationText(locale, value) };
}

/**
 * Comparación de dos versiones con lo que se muestra (C32 1.14.9): una medida cambió solo si su número mostrado cambió
 * («0.4 %» y «0.4 %» no cambió). Empeoró si las dos subieron (o una subió y la otra igual), mejoró si bajaron, resultado
 * mixto si van en sentidos opuestos, sin cambios si ninguna se ve distinta.
 */
export function releaseTrend(
  current: HubRelease,
  previous: HubRelease,
  locale: Locale = "es-PE",
): "same" | "worse" | "better" | "mixed" {
  const rateShown = percentText(locale, current.errorRate) !== percentText(locale, previous.errorRate);
  const p95Shown = durationText(locale, current.p95) !== durationText(locale, previous.p95);
  const rate = rateShown ? (current.errorRate ?? 0) - (previous.errorRate ?? 0) : 0;
  const p95 = p95Shown ? (current.p95 ?? 0) - (previous.p95 ?? 0) : 0;
  if (rate === 0 && p95 === 0) return "same";
  if (rate >= 0 && p95 >= 0) return "worse";
  if (rate <= 0 && p95 <= 0) return "better";
  return "mixed";
}

/**
 * RC1 · K3 (AF4, D7, E26): el nombre de cada operación en palabras, por su ruta. La clave es «MÉTODO ruta» (como la
 * registra el laboratorio) o solo la ruta (como la registra el Error Hub en cada servicio: el patrón de la ruta, sin
 * método); minúsculas y sin «/» al principio. La ruta se ve solo en el contexto técnico de Explorar.
 */
const operationKeys: Record<string, string> = {
  "post api/knowledge/index": "KNOWLEDGE_INDEX",
  "post api/media/upload": "MEDIA_UPLOAD",
  "post api/agent/turn": "AGENT_TURN",
  "post api/employees/tools": "EMPLOYEE_TOOL",
  "post api/messages/send": "MESSAGE_SEND",
  "post api/identity/login": "SIGN_IN",
  "get api/ai/usage/summary": "AI_USAGE",
  "get api/approvals": "APPROVALS",
  "get api/crm/contacts": "CRM_CONTACTS",
  "post api/crm/stage": "CRM_STAGE",
  "get api/conversations": "CONVERSATIONS",
  "get api/inboxes": "INBOXES",
  "get api/organizations/page": "ORGANIZATIONS",
  "get api/texts": "TEXTS",
  unmatched: "UNMATCHED",
};

const routeKey = (operation: string) =>
  operation
    .trim()
    .toLowerCase()
    .replace(/\s+\/+/, " ")
    .replace(/^\/+/, "");

/** La clave del catálogo para una operación (con o sin método), o null si la ruta no tiene nombre. */
export function operationKey(operation: string | null | undefined): string | null {
  if (!operation) return null;
  const key = routeKey(operation);
  const code = operationKeys[key] ?? operationKeys[key.replace(/^(get|post|put|patch|delete) /, "")];
  if (code) return `ADM_HUB_OP_${code}`;
  // Solo la ruta (sin método): la primera entrada del catálogo con esa ruta.
  const match = Object.entries(operationKeys).find(
    ([route]) => route.replace(/^(get|post|put|patch|delete) /, "") === key,
  );
  return match ? `ADM_HUB_OP_${match[1]}` : null;
}

/** El nombre de la operación en palabras; una ruta sin nombre en el catálogo queda con `known: false` (va a pendientes). */
export function operationName(L: Label, operation: string | null | undefined): { text: string; known: boolean } {
  const key = operationKey(operation);
  const text = key ? L(key) : "";
  if (key && text && !text.startsWith("⟦")) return { text, known: true };
  return { text: operation?.trim() || "—", known: false };
}

/**
 * «Buscar operación» busca por el nombre que se ve: si el texto está en el nombre de una sola operación del catálogo, el
 * Error Hub recibe su ruta; si no, el texto tal cual (la ruta también se puede escribir).
 */
export function operationSearch(L: Label, text: string): string {
  const fold = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const wanted = fold(text.trim());
  if (!wanted) return text;
  const routes = Object.entries(operationKeys)
    .filter(([, code]) => {
      const name = L(`ADM_HUB_OP_${code}`);
      return !name.startsWith("⟦") && fold(name).includes(wanted);
    })
    .map(([route]) => route.replace(/^(get|post|put|patch|delete) /, ""));
  return routes.length === 1 ? routes[0]! : text;
}
